/**
 * Fuentes de cuentas estándar de LeanTek.
 *
 * Todas las apps que usan el selector de cuentas muestran las mismas dos
 * fuentes (Tech y Ops), así que sus ids, textos y tonos viven aquí y no en cada
 * proyecto. Un proyecto traduce sus ids internos a estos (`tech` / `ops`) en sus
 * `scopes` y el Sidenav se encarga del resto: chips de filtro, toggle de fuente
 * y badges de cada cuenta.
 *
 * Los textos se cambian por parámetro con `accountSourceLabels`; las listas
 * completas se siguen pudiendo reemplazar con `accountFilters` / `accountSources`.
 */

export const ACCOUNT_SOURCE_IDS = Object.freeze({
  ALL: 'all',
  TECH: 'tech',
  OPS: 'ops',
});

export const DEFAULT_ACCOUNT_SOURCES = Object.freeze([
  Object.freeze({ id: ACCOUNT_SOURCE_IDS.TECH, label: 'GTS', tone: 'info' }),
  Object.freeze({ id: ACCOUNT_SOURCE_IDS.OPS, label: 'GBS', tone: 'warning' }),
]);

export const DEFAULT_ACCOUNT_FILTERS = Object.freeze([
  Object.freeze({ id: ACCOUNT_SOURCE_IDS.ALL, label: 'All', tone: 'mixed' }),
  ...DEFAULT_ACCOUNT_SOURCES,
]);

/**
 * Aplica `labels` ({ [id]: texto }) sobre una lista de fuentes o filtros.
 * Solo toca los ids que aparecen en `labels`; el resto queda igual.
 */
export function applyAccountSourceLabels(list, labels) {
  if (!Array.isArray(list)) return list;
  if (!labels) return list.map((item) => ({ ...item }));
  return list.map((item) =>
    typeof labels[item.id] === 'string' ? { ...item, label: labels[item.id] } : { ...item }
  );
}

/**
 * Badge de una fuente estándar, o null si el id no es una fuente conocida.
 * Útil para proyectos que arman sus propios badges.
 */
export function getAccountSourceBadge(sourceId, labels = null) {
  const source = DEFAULT_ACCOUNT_SOURCES.find((s) => s.id === sourceId);
  if (!source) return null;
  const label = labels && typeof labels[sourceId] === 'string' ? labels[sourceId] : source.label;
  return { id: source.id, label, tone: source.tone };
}

/**
 * Si la cuenta no trae `badges` pero sí `sources` (ids de fuente), le genera
 * los badges estándar. Una cuenta con `badges` propios se respeta tal cual.
 */
export function withDefaultSourceBadges(account, labels = null) {
  if (!account || Array.isArray(account.badges) || !Array.isArray(account.sources)) {
    return account;
  }
  const badges = account.sources
    .map((id) => getAccountSourceBadge(id, labels))
    .filter(Boolean);
  return { ...account, badges };
}
